// src/pages/DJPublicPage.tsx
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { usersApi } from "../services/users";
import type { User } from "../types/User";
import "./DJPublicPage.css";
import { currencySymbol } from "../utils/currency";
import { FaInstagram, FaChevronDown, FaChevronUp, FaExternalLinkAlt } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { t } from "../i18n";
import { DEFAULT_AVATAR_SRC, showDefaultAvatar } from "../lib/defaultAvatar";

type DJProfile = User;

// Phase C: shared util — all 12 pay-in currencies, correct fallback.
const getSymbol = (code?: string) => currencySymbol(code || "usd");

const normalizeUrl = (v: string) => {
  const s = (v || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z]+:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const DJPublicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [dj, setDj] = useState<DJProfile | null>(null);
  const { user } = useContext(AuthContext);

  const [showLinks, setShowLinks] = useState(false);

  useEffect(() => {
    if (!slug) return;
    if (!slug) return;
    usersApi.get(slug).then((dj) => setDj(dj));
  }, [slug]);

  if (!dj) return <p style={{ textAlign: "center" }}>{t("dJPublicPage.loadingDj")}</p>;

  const isOwnProfile = user?.slug === dj.slug;
  const symbol = getSymbol(dj.currency || "usd");
  const handle = (dj.instagram || "").replace(/^@+/, "");
  const instaUrl = handle ? `https://instagram.com/${handle}` : "";

  const cleanedLinks = Array.isArray(dj.links)
    ? dj.links.map(normalizeUrl).filter((x) => !!x)
    : [];

  return (
    <div className="dj-public-container">
      <div className="dj-card-public">
        {dj.avatarUrl ? (
          <img
            src={dj.avatarUrl.startsWith("http") ? dj.avatarUrl : `https://api.requesta.app${dj.avatarUrl}`}
            alt={t("dJPublicPage.avatar")}
            className="dj-avatar"
            onError={showDefaultAvatar}
          />
        ) : (
          <img src={DEFAULT_AVATAR_SRC} alt={t("dJPublicPage.avatar")} className="dj-avatar" />
        )}

        <h2 className="dj-name-public">{dj.name}</h2>
        {dj.bio && (
          <p className="dj-bio-public">{dj.bio}</p>
        )}
        <p className="dj-price-public">
          {/* 0 is a real price: free. (It used to print "1".) */}
          {t("dJPublicPage.pricePerRequest")}<span>{Number(dj.pricePerRequest) > 0 ? `${symbol} ${dj.pricePerRequest}` : t("dJPublicPage.free")}</span>
        </p>

        {handle && (
          <a
            href={instaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-icon-link"
            title={`@${handle}`}
            aria-label={t("dJPublicPage.openInstagram", { handle })}
          >
            <FaInstagram size={28} />
          </a>
        )}

        {cleanedLinks.length > 0 && (
          <div className="links-section">
            <button
              className="links-toggle"
              onClick={() => setShowLinks((s) => !s)}
              aria-expanded={showLinks}
              aria-controls="dj-links-list"
            >
              <span>{t("dJPublicPage.links")}</span>
              {showLinks ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {showLinks && (
              <ul id="dj-links-list" className="links-list-public">
                {cleanedLinks.map((u, i) => (
                  <li key={i}>
                    <a href={u} target="_blank" rel="noopener noreferrer">
                      {u}
                      <FaExternalLinkAlt size={12} style={{ marginLeft: 6 }} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!isOwnProfile && (
          <button className="request-button" onClick={() => (window.location.href = `/dj/${dj.slug}/request`)}>
            {t("dJPublicPage.makeARequest")}
          </button>
        )}
      </div>
    </div>
  );
};

export default DJPublicPage;
